import { DateTime } from 'luxon';

import { ColumnType } from 'types/column';
import { FilterInput, OPERATORS_WITHOUT_VALUE } from 'dashboards/types/filter';

import { pluralize } from 'services/string';

function isValidValueForDatatype(value: string, datatype: ColumnType): boolean {
  if (!value) return true;

  switch (datatype) {
    case 'BOOLEAN':
      return value === 'true' || value === 'false';
    case 'INT8':
    case 'INT16':
    case 'INT32':
    case 'INT64':
      return Number.isInteger(Number(value));
    case 'FLOAT':
    case 'DOUBLE':
      return Number.isFinite(Number(value));
    case 'DATE':
      return DateTime.fromISO(value).isValid;
    case 'TIMESTAMP':
      return DateTime.fromSeconds(Number(value)).isValid;
    case 'JSON':
      try {
        JSON.parse(value);
        return true;
      } catch {
        return false;
      }
    case 'STRING':
      return true;
    default:
      return false;
  }
}

function hasInvalidFields(filters: FilterInput[]): boolean {
  return filters.some(({ column, operator, value, and, or }) => {
    if (!column || (!value && !OPERATORS_WITHOUT_VALUE.includes(operator))) {
      return true;
    }

    if (and) {
      return hasInvalidFields(and);
    }

    if (or) {
      return hasInvalidFields(or);
    }

    return false;
  });
}

/** Check if filter exists with a column that doesn't exist on the current metric or table */
function hasNonExistentColumns(
  filters: FilterInput[],
  columns: Array<{
    name: string;
    datatype: ColumnType;
  }>
) {
  const nonExistentColumns: string[] = [];

  const checkColumns = (filterArray: FilterInput[]) => {
    filterArray.forEach((filter) => {
      // Check if the current filter's column exists in the columns array
      const columnExists = columns.some(
        (column) => column.name === filter.column
      );

      // If column doesn't exist and isn't already in the list, add it
      if (!columnExists && !nonExistentColumns.includes(filter.column)) {
        nonExistentColumns.push(filter.column);
      }

      // Recursively check AND and OR nested filters
      if (filter.and) {
        checkColumns(filter.and);
      }

      if (filter.or) {
        checkColumns(filter.or);
      }
    });
  };

  checkColumns(filters);

  return nonExistentColumns;
}

function hasInvalidDatatypes(
  filters: FilterInput[],
  columns: Array<{ name: string; datatype: ColumnType }>
): string[] {
  const invalidValues: string[] = [];

  const checkDatatypes = (filterInput: FilterInput[]) => {
    filterInput.forEach((filter) => {
      if (filter.value && !OPERATORS_WITHOUT_VALUE.includes(filter.operator)) {
        const column = columns.find((column) => column.name === filter.column);

        if (column && !isValidValueForDatatype(filter.value, column.datatype)) {
          invalidValues.push(
            `${filter.column}: "${filter.value}" (expected ${DATATYPE_MAP[column.datatype]})`
          );
        }
      }

      if (filter.and) checkDatatypes(filter.and);
      if (filter.or) checkDatatypes(filter.or);
    });
  };

  checkDatatypes(filters);
  return invalidValues;
}

/** Validation function that can be passed as a `Controller` validation rule */
export function validateDataWidgetFilters(
  filters: FilterInput[] = [],
  columns: Array<{
    name: string;
    datatype: ColumnType;
  }>
) {
  if (hasInvalidFields(filters)) {
    return 'Contains invalid filters.';
  }

  const nonExistentColumns = hasNonExistentColumns(filters, columns);

  if (nonExistentColumns.length) {
    return `Contains filters with non-existent ${pluralize(nonExistentColumns.length, 'column')}: ${nonExistentColumns.join(', ')}`;
  }

  const invalidDatatypes = hasInvalidDatatypes(filters, columns);

  if (invalidDatatypes.length) {
    return `Contains filters with invalid ${pluralize(invalidDatatypes.length, 'value')}: ${invalidDatatypes.join(', ')}`;
  }
}

const DATATYPE_MAP: Record<ColumnType, string> = {
  BOOLEAN: 'true or false',
  DATE: 'ISO 8601 date',
  DOUBLE: 'number',
  FLOAT: 'number',
  INT16: 'number',
  INT32: 'number',
  INT64: 'number',
  INT8: 'number',
  JSON: 'JSON string',
  STRING: 'text string',
  TIMESTAMP: 'timestamp',
};
